import React from "react"
import { useId } from "@reach/auto-id"
import { isNotEmpty } from "utils/validation"
import { useTranslation } from "react-i18next"
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input"
import { FormFields, FormRule, FormError } from "models/form"
import {
	handleInternalInputValidation,
	FormFieldFloatingLabel,
	StatesAndHelpersFloatingLabel,
} from "../formUtils"
import { Errors } from "../Errors"

import "react-phone-number-input/style.css"
import "./InputPhone.scoped.scss"
import { CountryCode, E164Number } from "libphonenumber-js"

export type InputPhoneProps = FormFields & {
	value: E164Number
	maxlength?: number
	defaultCountry?: CountryCode
	country?: string
	classNameInput?: string
	isResetField?: boolean
}

export const InputPhone: React.FC<InputPhoneProps> = React.forwardRef(
	(
		{
			// Attributes
			id,
			value = "",
			name,
			label = "",
			// placeholder = "06 00 00 00 00",
			placeholder = "",
			disabled,
			maxlength = 14,
			defaultCountry = "FR",
			isResetField,
			// Styles
			style,
			className = "",
			classNameLabel = "",
			classNameInput = "",
			forceFloating = false,
			// Error management
			required = false,
			customRules,
			showErrors,
			// Handler
			onChange,
			onFocus,
			onBlur,
			onValidate,
		},
		ref: React.LegacyRef<HTMLInputElement>
	) => {
		const INPUT_TYPE = "tel"
		const prefix = useId()
		const inputId = id ?? `${prefix}-${name}`
		const errorId = `${inputId}-error`

		const [phone, setPhone] = React.useState<E164Number>(value)

		React.useEffect(() => {
			if (isResetField) setPhone(null)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isResetField])

		const inputRef = React.useRef(null)
		// React.useImperativeHandle(
		// 	ref as React.Ref<Partial<HTMLInputElement>>,
		// 	() => ({
		// 		// if a parent component want be able to access inner ref with
		// 		// a forwadedRef and for instance call inputRef.current.focus()
		// 		// See https://reactjs.org/docs/hooks-reference.html#useimperativehandle
		// 		focus: () => inputRef.current.focus(),
		// 	})
		// )
		// const inputRef = ref ?? React.useRef(null)

		const [errors, setErrors] = React.useState<FormError[]>([])
		const { t } = useTranslation()

		const rules: FormRule[] = React.useMemo(
			() => [
				{
					key: "input-empty",
					check: (value: string) => (required ? isNotEmpty(value) : true),
					message: t("error_mandatory_field", {
						ns: "form",
						fieldName: label,
					}),
				},
				{
					key: "valid-value",
					check: (value: string) => isValidPhoneNumber(value),
					message: t("error_invalid_phone_number", {
						ns: "form",
					}),
				},
				...(customRules || []),
			],
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[required]
		)
		// customRules in dependencies array trigger an infinite loop,
		// because custom rules include a check property defining a new function,
		// preventing useMemo to return a valid shallow-comparison
		// or even useDeepMemo to return a valid deep-comparison

		const validate = React.useCallback(
			(currentValue: E164Number) =>
				handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
			[rules, name, onValidate]
		)

		// Validate on mount and on each value change
		React.useEffect(() => {
			validate(value)

			// TODO: get country code + add maxLength
			// if the value is externally changed (by the parent or another comp)
			// register the value in the local state to reflect it in the comp
			if (value && phone !== value) {
				setPhone(value)
			}

			return () => {
				// On unmounted errors and validation are no longer needed
				setErrors([])
				if (onValidate) onValidate(name, [])
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [value])

		// intermediary method to provide the form's onChange function
		// with the expected value format
		const handleChange = (value: E164Number) => {
			const inputValue = {
				target: {
					name: name,
					value,
				},
			}
			setPhone(value)
			onChange && onChange(inputValue)
		}

		// On resetField
		React.useEffect(() => {
			if (isResetField) setPhone(null)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isResetField])

		// Custom input to manage maxLength
		// ------------------------
		const CustomInput = React.useMemo(() => {
			return React.forwardRef((props, ref: React.LegacyRef<HTMLInputElement>) => (
				<input ref={ref} maxLength={maxlength} {...props} />
			))
		}, [maxlength])

		// When the inner input is focused by a key, there is currently no mean
		// to tell the parent to adjuste css.
		// Expecting :focus-visible-within https://github.com/WICG/focus-visible/issues/151
		const [isFocusVisible, setIsFocusVisible] = React.useState<boolean>(false)
		const isFocusVisibleClassname = isFocusVisible ? "is-focus-visible-fix" : ""

		return (
			<FormFieldFloatingLabel
				inputType={INPUT_TYPE}
				inputValue={phone}
				forceFloating={forceFloating}
			>
				{({
					floatingLabelClassname,
					onFloatingLabelFocus,
					onFloatingLabelBlur,
					inputAttributeToFixAutofill,
				}: StatesAndHelpersFloatingLabel) => (
					<div
						className={`c-form-field ${className} ${floatingLabelClassname}`}
						// @ts-expect-error: custom properties are not considered as CSS property
						style={{ ...style, "--labelOffsetX": "60px" }}
					>
						<label
							className={`c-form-field__label c-label px-2 ${classNameLabel}`}
							htmlFor={inputId}
						>
							{label} {required && "*"}
						</label>

						<PhoneInput
							className={`
							c-form-field__form c-input-phone px-5
							${showErrors && errors.length > 0 ? "has-error " : ""}
							${classNameInput ? classNameInput : ""}
							${disabled ? "is-disabled " : ""}
							${isFocusVisibleClassname}
						`}
							id={inputId}
							ref={inputRef}
							name={name}
							placeholder={placeholder}
							// @ts-ignore: FIXME phone should only be a E164Number ?
							value={phone}
							defaultCountry={defaultCountry}
							// @ts-ignore
							country={defaultCountry}
							international={false}
							disabled={disabled}
							required={required}
							onChange={(event) => {
								!disabled && onChange && handleChange(event)
							}}
							onFocus={(event) => {
								onFloatingLabelFocus()
								setIsFocusVisible(true)
								onFocus && onFocus(event)
							}}
							onBlur={(event) => {
								onFloatingLabelBlur()
								setIsFocusVisible(false)
								onBlur && onBlur(event)
							}}
							inputComponent={CustomInput}
							{...inputAttributeToFixAutofill}
							aria-describedby={errorId}
							aria-invalid={Boolean(errors.length)}
						/>

						{/* Error messages */}
						{showErrors && <Errors id={errorId} errors={errors} />}
					</div>
				)}
			</FormFieldFloatingLabel>
		)
	}
)
